/**
 * Scroll vertical manual en móvil cuando hijos interactivos bloquean el scroll nativo.
 * Patrón: docs/51-ANALISIS_COMPLETO_SCROLL_DASHBOARD_CAMERA_TAB.md §19 (Automatizaciones)
 * y CameraTab.tsx (scroll incremental + momentum 0.95).
 */
import { useEffect, useRef } from 'react';
import { markUiScrolling } from '../utils/componentUpdateRegistry';

type TouchStartState = {
  x: number;
  y: number;
  scrollTop: number;
  time: number;
};

export const TOUCH_SCROLL_PANEL_SX = {
  flex: 1,
  minHeight: 0,
  height: 0,
  overflowY: 'auto',
  overflowX: 'hidden',
  WebkitOverflowScrolling: 'touch',
  touchAction: 'pan-y',
  overscrollBehaviorY: 'contain',
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
  '&::-webkit-scrollbar': { display: 'none', width: 0, height: 0 },
} as const;

export function useManualTouchScroll(enabled = true) {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!enabled) return undefined;

    const attachListeners = (): (() => void) | undefined => {
      const container = scrollRef.current;
      if (!container) return undefined;

      const isDndLocked = () =>
        document.documentElement.classList.contains('shc-dnd-lock');

      const isNestedInnerScrollTarget = (target: EventTarget | null) => {
        if (!(target instanceof Element)) return false;
        if (target.closest(
          '[data-automation-drag-handle], [data-widget-drag-handle], [data-widget-resize-handle], [data-climate-drag-handle]',
        )) return true;
        const inner = target.closest(
          '[data-audio-inner-scroll], [data-settings-room-inner-scroll], [data-favorites-room-inner-scroll]',
        );
        return inner != null && inner !== container;
      };

      let touchStart: TouchStartState | null = null;
      let isScrolling = false;
      let velocity = 0;
      let momentumRafId: number | null = null;

      const stopMomentum = () => {
        if (momentumRafId !== null) {
          cancelAnimationFrame(momentumRafId);
          momentumRafId = null;
        }
        velocity = 0;
      };

      const resetGesture = () => {
        touchStart = null;
        isScrolling = false;
      };

      const handleTouchStart = (e: TouchEvent) => {
        if (isDndLocked() || isNestedInnerScrollTarget(e.target)) return;
        if (!e.touches[0]) return;
        if (momentumRafId !== null) {
          stopMomentum();
          isScrolling = true;
        }
        touchStart = {
          x: e.touches[0].clientX,
          y: e.touches[0].clientY,
          scrollTop: container.scrollTop,
          time: Date.now(),
        };
        isScrolling = false;
      };

      const handleTouchMove = (e: TouchEvent) => {
        if (isDndLocked() || isNestedInnerScrollTarget(e.target)) return;
        if (!touchStart || !e.touches[0]) return;

        const deltaX = e.touches[0].clientX - touchStart.x;
        const deltaY = e.touches[0].clientY - touchStart.y;
        const absDeltaX = Math.abs(deltaX);
        const absDeltaY = Math.abs(deltaY);

        if (absDeltaY > 2 || absDeltaX > 2) {
          isScrolling = true;
        }

        if (absDeltaY > absDeltaX && absDeltaY > 5) {
          const timeDelta = Date.now() - touchStart.time;
          if (timeDelta > 0) {
            velocity = deltaY / timeDelta;
          }

          const maxScroll = Math.max(0, container.scrollHeight - container.clientHeight);
          container.scrollTop = Math.max(
            0,
            Math.min(maxScroll, container.scrollTop - deltaY),
          );
          markUiScrolling(220);

          touchStart = {
            x: e.touches[0].clientX,
            y: e.touches[0].clientY,
            scrollTop: container.scrollTop,
            time: Date.now(),
          };

          e.preventDefault();
          e.stopPropagation();
        }
      };

      const handleTouchEnd = () => {
        if (isScrolling && velocity !== 0) {
          const friction = 0.95;
          let currentVelocity = velocity * 16;
          const tick = () => {
            if (Math.abs(currentVelocity) <= 0.5) {
              momentumRafId = null;
              return;
            }
            markUiScrolling(220);
            const maxScroll = Math.max(0, container.scrollHeight - container.clientHeight);
            container.scrollTop = Math.max(
              0,
              Math.min(maxScroll, container.scrollTop - currentVelocity),
            );
            currentVelocity *= friction;
            momentumRafId = requestAnimationFrame(tick);
          };
          stopMomentum();
          momentumRafId = requestAnimationFrame(tick);
        }
        window.setTimeout(resetGesture, 100);
      };

      const capture = { capture: true } as const;
      container.addEventListener('touchstart', handleTouchStart, { ...capture, passive: true });
      container.addEventListener('touchmove', handleTouchMove, { ...capture, passive: false });
      container.addEventListener('touchend', handleTouchEnd, { ...capture, passive: true });
      container.addEventListener('touchcancel', handleTouchEnd, { ...capture, passive: true });

      return () => {
        container.removeEventListener('touchstart', handleTouchStart, capture);
        container.removeEventListener('touchmove', handleTouchMove, capture);
        container.removeEventListener('touchend', handleTouchEnd, capture);
        container.removeEventListener('touchcancel', handleTouchEnd, capture);
        stopMomentum();
        resetGesture();
      };
    };

    let teardown = attachListeners();
    let mountRafId = 0;
    if (!teardown) {
      mountRafId = requestAnimationFrame(() => {
        teardown = attachListeners();
      });
    }

    return () => {
      if (mountRafId) cancelAnimationFrame(mountRafId);
      teardown?.();
    };
  }, [enabled]);

  return scrollRef;
}

/** Marca scroll activo; pausar refrescos de UI mientras el usuario desliza. */
export function useScrollActivityRef(idleMs = 250) {
  const activeRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const markScrolling = () => {
    activeRef.current = true;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      activeRef.current = false;
    }, idleMs);
  };

  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  return { activeRef, markScrolling };
}
